import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getAsset } from '../../../../../../../../../util/assets';
import Button from '../../../../../../../../../components/global/Button';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Badge from '../../../../../../../../../components/global/Badge';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import EventCollagesModal from './components/EventCollages';
import AccountCardAlbumCustomPrintConfiguratorModal from '../AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import { CollageDesignerSuccessModal } from '../../../../../../../../../components/global/CollageDesigner/components/CollageDesignerSuccess';
import EventCollageSuccessModal from './components/EventCollageSuccessModal';
import { isPictureDayMembership } from '../../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  className: PropTypes.string,
  eventCollages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      preview_image: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  ),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  isAllPhotos: PropTypes.bool,
};

const EventCollagesOption = ({
  photo,
  className,
  eventCollages,
  identity,
  albumMembershipGroup,
  onAlbumCustomPrintToggle,
  onCheckout,
  readOnly,
  disabled,
  isAllPhotos,
}) => {
  const openModal = useOpenModal();
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [collageModalOpen, setCollageModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  const [selectedEventCollage, setSelectedEventCollage] = useState(null);
  const [completedAccountCard, setCompletedAccountCard] = useState(null);

  const isPictureDay = isPictureDayMembership(albumMembershipGroup);
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-event-collage': true,
  });

  return (
    <Fragment>
      <div className={classes} data-testid="collages-option">
        {eventCollages.length === 1 && (
          <Fragment>
            <div className="purchasing-option-item-thumb">
              <img
                width={
                  eventCollages[0].custom_print.preview_image?.url ? 80 : 60
                }
                src={
                  eventCollages[0].custom_print.preview_image?.url ||
                  getAsset('/plaque.svg')
                }
                alt=""
              />
            </div>

            <div className="purchasing-option-item-content-wrap">
              <span className="purchasing-option-item-title">
                {eventCollages[0].custom_print.name}
              </span>

              {!readOnly && (
                <Button
                  className={classNames({
                    'purchasing-options-item-btn': true,
                    'mt-2': true,
                  })}
                  data-testid="btn-event-collages-shop"
                  onClick={() => {
                    if (disabled) {
                      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
                        name: eventCollages[0].custom_print.name,
                      });
                      return;
                    }

                    setSelectedEventCollage(eventCollages[0]);
                    setCollageModalOpen(true);
                  }}
                >
                  Configure &amp; Buy
                </Button>
              )}
              {readOnly && (
                <div data-testid="collages-price-list">
                  <div className="d-flex flex-column mt-1">
                    <span className="text-sm purchasing-option-item-title">
                      {eventCollages[0].custom_print.name}
                    </span>
                    <div className="purchasing-option-item-info-extra">
                      <Badge badgeStyle="yellow" className="text-extra-bold">
                        <PriceDisplay
                          price={eventCollages[0].custom_print.price.value}
                          currency={
                            eventCollages[0].custom_print.price.currency
                          }
                        />
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Fragment>
        )}
        {eventCollages.length > 1 && (
          <Fragment>
            <div className="purchasing-option-item-thumb">
              <img
                width={60}
                src={getAsset('/event-collages-thumb.png')}
                alt=""
              />
            </div>

            <div className="purchasing-option-item-content-wrap">
              <span className="purchasing-option-item-title">
                Event Collages
              </span>
              <div className="purchasing-option-item-info-extra">
                <span className="text-primary text-sm text-extra-bold">
                  {pluralize('product', eventCollages?.length, true)}
                </span>
              </div>

              {!readOnly && (
                <Button
                  className={classNames({
                    'purchasing-options-item-btn': true,
                    'mt-2': true,
                  })}
                  data-testid="btn-event-collages-shop"
                  onClick={() => {
                    if (disabled) {
                      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
                        name: 'Event collages',
                      });
                      return;
                    }

                    setSelectionModalOpen(true);
                  }}
                >
                  {eventCollages.length === 1 ? 'Configure & Buy' : 'Shop'}
                </Button>
              )}
              {readOnly && (
                <div data-testid="collages-price-list">
                  {(eventCollages.records ?? []).map(eventCollage => {
                    return (
                      <div
                        key={eventCollage.id}
                        className="d-flex flex-column mt-1"
                      >
                        <span className="text-sm purchasing-option-item-title">
                          {eventCollage.custom_print.name}
                        </span>
                        <div className="purchasing-option-item-info-extra">
                          <Badge
                            badgeStyle="yellow"
                            className="text-extra-bold"
                          >
                            <PriceDisplay
                              price={eventCollage.custom_print.price.value}
                              currency={
                                eventCollage.custom_print.price.currency
                              }
                            />
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Fragment>
        )}
      </div>
      <EventCollagesModal
        forceOpen={selectionModalOpen}
        photo={photo}
        eventCollages={eventCollages}
        onClose={() => setSelectionModalOpen(false)}
        onDismiss={() => {
          setSelectionModalOpen(false);
          setSelectedEventCollage(null);
        }}
        onEventCollageSelect={eventCollage => {
          setSelectedEventCollage(eventCollage);
          setSelectionModalOpen(false);
          setCollageModalOpen(true);
        }}
      />
      <AccountCardAlbumCustomPrintConfiguratorModal
        forceOpen={collageModalOpen}
        albumCustomPrint={selectedEventCollage}
        onClose={() => {
          setCollageModalOpen(false);
        }}
        onFinished={({ accountCard }) => {
          setCompletedAccountCard(accountCard);
          setCollageModalOpen(false);
          setSuccessModalOpen(true);
        }}
        defaultAccountCard={completedAccountCard}
        deleteAccountCardOnDismiss
        identity={identity}
        albumMembershipGroup={albumMembershipGroup}
        buttonText="Create my Camper Collage"
        inlineButton
        isAllPhotos={isAllPhotos}
        forceEntitlement={!isPictureDay}
        title="Design your collage!"
        altClassName="event-collage-option-account-card-configurator-modal"
      />
      <CollageDesignerSuccessModal
        forceOpen={successModalOpen}
        accountCard={completedAccountCard}
        title="Looks great!"
        onAddToCart={() => {
          onAlbumCustomPrintToggle(selectedEventCollage, {
            accountCard: completedAccountCard,
          });
          setSuccessModalOpen(false);
          setCheckoutModalOpen(true);
        }}
        onGoBack={() => {
          setSuccessModalOpen(false);
          setCollageModalOpen(true);
        }}
        onClose={() => {
          setSuccessModalOpen(false);
        }}
      />
      {checkoutModalOpen && (
        <EventCollageSuccessModal
          forceOpen
          eventCollage={selectedEventCollage}
          onCheckout={() => {
            setSelectedEventCollage(null);
            setCompletedAccountCard(null);
            setCheckoutModalOpen(false);
            onCheckout();
          }}
          onDismiss={() => {
            setCheckoutModalOpen(false);
          }}
        />
      )}
    </Fragment>
  );
};

EventCollagesOption.propTypes = propTypes;

export default EventCollagesOption;
